type Primitive = 'string' | 'number' | 'boolean';

export type Def = {
  [K: string]: Primitive | Def;
};

export type InferSchema<T> = T extends Schema<infer U> ? U : never;

type InferType<T extends Def> = {
  [K in keyof T]: T[K] extends Primitive
    ? T[K] extends 'string'
      ? string
      : T[K] extends 'number'
      ? number
      : boolean
    : T[K] extends Def
    ? InferType<T[K]>
    : never;
};

class Schema<T extends Def> {
  constructor(private def: T) {}

  /**
   * This is a helper to get the type of the schema.
   * Don't call this at runtime, it will throw an error.
   */
  public get infer(): InferType<T> {
    throw new Error('Not implemented');
  }

  public validate(value: unknown): value is InferType<T> {
    return this.run(value);
  }

  private run(value: unknown): value is InferType<T> {
    if (typeof value !== 'object' || value === null) {
      return false;
    }

    return Object.entries(this.def).every(([key, type]) => {
      if (!(key in value)) {
        return false;
      }

      if (typeof type === 'object') {
        const nested = new Schema(type);
        return nested.run((value as never)[key]);
      }

      return typeof (value as never)[key] === type;
    });
  }
}

export function schema<T extends Def>(def: T): Schema<T> {
  return new Schema(def);
}
